<template>
  <Dialog
    v-model:visible="displayModal"
    :header="file?.name || t('fileManager.imageCompression.title')"
    :modal="true"
    :closable="true"
    :draggable="false"
    style="width: 900px; max-width: 95vw"
    @update:visible="handleClose"
  >
    <div class="flex flex-column gap-3">
      <!-- Message explicatif -->
      <div class="info-message">
        <i class="fal fa-info-circle mr-2"></i>
        <span>{{ t('fileManager.imageCompression.explanationMessage') }}</span>
      </div>

      <!-- Toggle Original / Compressed -->
      <div class="flex justify-content-end">
        <SelectButton
          v-model="selectedVersion"
          :options="versionOptions"
          option-value="value"
          :allow-empty="false"
          :unselectable="false"
        >
          <template #option="{ option }">
            <span>{{ t(`fileManager.imageCompression.${option.value}`) }}</span>
            <span
              v-if="option.value === 'compressed'"
              class="recommended-badge"
            >
              {{ t('fileManager.imageCompression.recommended') }}
            </span>
          </template>
        </SelectButton>
      </div>

      <!-- Image Comparison -->
      <div class="grid">
        <!-- Original -->
        <div
          class="col-12 md:col-6"
          :class="{ 'opacity-30': selectedVersion !== 'original' }"
        >
          <div class="flex flex-column gap-2">
            <h3 class="m-0">{{ t('fileManager.imageCompression.original') }}</h3>
            <div class="image-preview-container" @click="zoomImage(originalPreview)">
              <img
                v-if="originalPreview"
                :src="originalPreview"
                :alt="t('fileManager.imageCompression.original')"
                class="preview-image"
              />
              <div class="zoom-overlay">
                <i class="fal fa-search-plus"></i>
              </div>
            </div>
            <div class="flex flex-column gap-1">
              <span class="text-sm text-600">
                {{ originalDimensions.width }} × {{ originalDimensions.height }}
              </span>
              <span class="text-sm font-semibold">
                {{ formatFileSize(originalSize) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Compressed -->
        <div
          class="col-12 md:col-6"
          :class="{ 'opacity-30': selectedVersion !== 'compressed' }"
        >
          <div class="flex flex-column gap-2">
            <h3 class="m-0">{{ t('fileManager.imageCompression.compressed') }}</h3>
            <div class="image-preview-container" @click="!compressing && zoomImage(compressedPreview)">
              <ProgressSpinner
                v-if="compressing"
                class="progress-spinner"
                style="width: 50px; height: 50px"
                stroke-width="4"
              />
              <img
                v-else-if="compressedPreview"
                :src="compressedPreview"
                :alt="t('fileManager.imageCompression.compressed')"
                class="preview-image"
              />
              <div v-if="!compressing" class="zoom-overlay">
                <i class="fal fa-search-plus"></i>
              </div>
            </div>
            <div class="flex flex-column gap-1">
              <span class="text-sm text-600">
                {{ compressedDimensions.width }} × {{ compressedDimensions.height }}
              </span>
              <span class="text-sm font-semibold" style="color: var(--primary-color)">
                {{ formatFileSize(compressedSize) }}
                <span v-if="compressionRatio > 0" class="ml-1">
                  (-{{ compressionRatio }}%)
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-end gap-2 w-full">
        <Button
          :label="t('cancel')"
          icon="fal fa-times"
          class="p-button-secondary"
          @click="handleClose"
        />
        <Button
          :label="t('fileManager.imageCompression.import')"
          icon="fal fa-check"
          :disabled="!canImport"
          @click="handleImport"
        />
      </div>
    </template>
  </Dialog>

  <!-- Zoom Dialog -->
  <Dialog
    v-model:visible="showZoom"
    :modal="true"
    :closable="true"
    :draggable="false"
    style="width: 90vw; max-width: 1200px"
    :header="t('fileManager.imageCompression.zoomTitle')"
  >
    <div class="zoom-image-container">
      <img
        v-if="zoomedImage"
        :src="zoomedImage"
        alt="Zoomed"
        class="zoomed-image"
      />
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  Ref,
  computed,
  watch,
  PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import SelectButton from 'primevue/selectbutton';
import ProgressSpinner from 'primevue/progressspinner';

export default defineComponent({
  name: 'ImageCompressionModal',

  components: {
    Dialog,
    Button,
    SelectButton,
    ProgressSpinner,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },
    file: {
      type: Object as PropType<globalThis.File | null>,
      required: false,
      default: null,
    },
    context: {
      type: String as PropType<'email' | 'display' | 'file-manager'>,
      required: false,
      default: 'file-manager',
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-import': (file: globalThis.File, isCompressed: boolean) => true,
  },

  setup(props, context) {
    const { t } = useI18n();

    const displayModal = computed(() => props.displayDialog);
    const selectedVersion = ref('compressed');
    const versionOptions = ref([
      { label: 'Original', value: 'original' },
      { label: 'Compressé', value: 'compressed' },
    ]);

    const originalPreview: Ref<string | null> = ref(null);
    const compressedPreview: Ref<string | null> = ref(null);
    const originalSize = ref(0);
    const compressedSize = ref(0);
    const originalDimensions = ref({ width: 0, height: 0 });
    const compressedDimensions = ref({ width: 0, height: 0 });
    const compressing = ref(false);
    const compressedFile: Ref<globalThis.File | null> = ref(null);

    // Zoom functionality
    const showZoom = ref(false);
    const zoomedImage: Ref<string | null> = ref(null);

    const compressionRatio = computed(() => {
      if (originalSize.value === 0) return 0;
      const ratio = ((originalSize.value - compressedSize.value) / originalSize.value) * 100;
      return Math.round(ratio);
    });

    const canImport = computed(() => {
      if (compressing.value) return false;
      if (selectedVersion.value === 'compressed') {
        return compressedFile.value !== null;
      }
      return props.file !== null;
    });

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const loadImageDimensions = (file: globalThis.File): Promise<{ width: number; height: number }> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    };

    const compressImage = async (file: globalThis.File): Promise<globalThis.File> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const img = new Image();
          img.onload = () => {
            let { width, height } = img;
            let quality = 0.85;

            if (props.context === 'email') {
              // Email : max 800px de large (standard email)
              const maxWidth = 900;

              // Ne réduire que si l'image est plus large que 800px
              if (width > maxWidth) {
                const ratio = maxWidth / width;
                width = maxWidth;
                height = Math.round(height * ratio);
              }

              // Qualité selon taille du fichier
              if (file.size > 2 * 1024 * 1024) { // > 2MB
                quality = 0.7;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                quality = 0.75;
              } else if (file.size > 500 * 1024) { // > 500KB
                quality = 0.8;
              } else {
                quality = 0.85;
              }
            } else if (props.context === 'display') {
              // Display : privilégier la qualité avec ratio de réduction
              let reductionRatio = 1.0;

              if (file.size > 2 * 1024 * 1024) { // > 2MB
                reductionRatio = 0.6;
                quality = 0.75;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                reductionRatio = 0.8;
                quality = 0.85;
              } else {
                reductionRatio = 1.0;
                quality = 0.9;
              }

              if (reductionRatio < 1.0) {
                width = Math.round(width * reductionRatio);
                height = Math.round(height * reductionRatio);
              }
            } else {
              // File Manager : modéré avec ratio de réduction
              let reductionRatio = 1.0;

              if (file.size > 2 * 1024 * 1024) { // > 2MB
                reductionRatio = 0.5;
                quality = 0.75;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                reductionRatio = 0.6;
                quality = 0.8;
              } else if (file.size > 500 * 1024) { // > 500KB
                reductionRatio = 0.8;
                quality = 0.85;
              } else {
                reductionRatio = 1.0;
                quality = 0.9;
              }

              if (reductionRatio < 1.0) {
                width = Math.round(width * reductionRatio);
                height = Math.round(height * reductionRatio);
              }
            }

            // Create canvas and compress
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Canvas context not available'));
              return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob with adaptive quality
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Compression failed'));
                  return;
                }
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              },
              file.type,
              quality
            );
          };
          img.onerror = reject;
          img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    const processImage = async (file: globalThis.File) => {
      if (!file) return;

      // Load original
      originalSize.value = file.size;
      originalPreview.value = URL.createObjectURL(file);
      originalDimensions.value = await loadImageDimensions(file);

      // Compress
      compressing.value = true;
      try {
        const compressed = await compressImage(file);

        // Vérifier si la compression a réduit la taille
        if (compressed.size < file.size) {
          // Compression réussie
          compressedFile.value = compressed;
          compressedSize.value = compressed.size;
          compressedPreview.value = URL.createObjectURL(compressed);
          compressedDimensions.value = await loadImageDimensions(compressed);
        } else {
          // La compression a augmenté la taille, utiliser l'original
          compressedFile.value = file;
          compressedSize.value = file.size;
          compressedPreview.value = URL.createObjectURL(file);
          compressedDimensions.value = originalDimensions.value;
        }
      } catch (error) {
        console.error('Compression error:', error);
        // En cas d'erreur, utiliser l'original
        compressedFile.value = file;
        compressedSize.value = file.size;
        compressedPreview.value = URL.createObjectURL(file);
        compressedDimensions.value = originalDimensions.value;
      } finally {
        compressing.value = false;
      }
    };

    const zoomImage = (imageUrl: string | null) => {
      if (imageUrl) {
        zoomedImage.value = imageUrl;
        showZoom.value = true;
      }
    };

    const handleClose = () => {
      // Cleanup URLs
      if (originalPreview.value) URL.revokeObjectURL(originalPreview.value);
      if (compressedPreview.value) URL.revokeObjectURL(compressedPreview.value);

      // Reset state
      originalPreview.value = null;
      compressedPreview.value = null;
      originalSize.value = 0;
      compressedSize.value = 0;
      originalDimensions.value = { width: 0, height: 0 };
      compressedDimensions.value = { width: 0, height: 0 };
      selectedVersion.value = 'compressed';
      compressedFile.value = null;

      context.emit('on-close-dialog');
    };

    const handleImport = () => {
      const fileToImport = selectedVersion.value === 'compressed' ? compressedFile.value : props.file;

      if (!fileToImport) {
        return;
      }

      context.emit('on-import', fileToImport, selectedVersion.value === 'compressed');
    };

    watch(
      () => props.file,
      (newFile) => {
        if (newFile && props.displayDialog) {
          processImage(newFile);
        }
      },
      { immediate: true }
    );

    return {
      t,
      displayModal,
      selectedVersion,
      versionOptions,
      originalPreview,
      compressedPreview,
      originalSize,
      compressedSize,
      originalDimensions,
      compressedDimensions,
      compressionRatio,
      compressing,
      canImport,
      formatFileSize,
      showZoom,
      zoomedImage,
      zoomImage,
      handleClose,
      handleImport,
    };
  },
});
</script>

<style lang="scss" scoped>
.info-message {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #e3f2fd;
  border-left: 4px solid #2196f3;
  border-radius: 4px;
  color: #1976d2;
  font-size: 14px;
  line-height: 1.5;
}

.recommended-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 3px 10px;
  background-color: white;
  color: var(--primary-color);
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid var(--primary-color);
}

.opacity-30 {
  opacity: 0.3;
  transition: opacity 0.3s ease;
}

.image-preview-container {
  position: relative;
  width: 100%;
  height: 250px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);

    .zoom-overlay {
      opacity: 1;
    }
  }
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.zoom-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  i {
    color: white;
    font-size: 16px;
  }
}

.progress-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.zoom-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.zoomed-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
</style>
